import React, { useEffect, useRef, useState } from "react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

const distanceBetweenTouches = (touches) => {
  const [first, second] = touches;
  return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
};

const PinchZoomImage = ({ src, alt, className }) => {
  const [scale, setScale] = useState(MIN_ZOOM);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const gesture = useRef({ startDistance: 0, startScale: MIN_ZOOM, startPosition: null, moved: false });
  const ignoreNextClick = useRef(false);

  useEffect(() => {
    const image = imageRef.current;
    const preventZoomClickAfterPinch = (event) => {
      if (!ignoreNextClick.current) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      ignoreNextClick.current = false;
    };

    image?.addEventListener("click", preventZoomClickAfterPinch, true);
    return () => image?.removeEventListener("click", preventZoomClickAfterPinch, true);
  }, []);

  const resetPositionIfNeeded = (nextScale) => {
    if (nextScale === MIN_ZOOM) setPosition({ x: 0, y: 0 });
  };

  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      gesture.current = {
        startDistance: distanceBetweenTouches(event.touches),
        startScale: scale,
        startPosition: null,
        moved: true,
      };
    } else if (event.touches.length === 1 && scale > MIN_ZOOM) {
      const touch = event.touches[0];
      gesture.current.startPosition = { x: touch.clientX, y: touch.clientY, offsetX: position.x, offsetY: position.y };
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 2 && gesture.current.startDistance) {
      event.preventDefault();
      const nextScale = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, gesture.current.startScale * (distanceBetweenTouches(event.touches) / gesture.current.startDistance))
      );
      setScale(nextScale);
      resetPositionIfNeeded(nextScale);
      return;
    }

    if (event.touches.length === 1 && scale > MIN_ZOOM && gesture.current.startPosition) {
      event.preventDefault();
      gesture.current.moved = true;
      const touch = event.touches[0];
      const { x, y, offsetX, offsetY } = gesture.current.startPosition;
      setPosition({ x: offsetX + touch.clientX - x, y: offsetY + touch.clientY - y });
    }
  };

  const handleTouchEnd = () => {
    if (gesture.current.moved) {
      ignoreNextClick.current = true;
      window.setTimeout(() => {
        ignoreNextClick.current = false;
      }, 400);
    }
    gesture.current.startDistance = 0;
    gesture.current.startPosition = null;
    gesture.current.moved = false;
  };

  return (
    <img
      ref={imageRef}
      className={className}
      src={src}
      alt={alt}
      loading="lazy"
      draggable="false"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onDoubleClick={() => {
        const nextScale = scale > MIN_ZOOM ? MIN_ZOOM : 2;
        setScale(nextScale);
        resetPositionIfNeeded(nextScale);
      }}
      style={{
        touchAction: "none",
        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
        transition: gesture.current.startDistance ? "none" : "transform 160ms ease-out",
        cursor: scale > MIN_ZOOM ? "grab" : "zoom-in",
      }}
    />
  );
};

export default PinchZoomImage;
